import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { ZAMBIAN_CITIES } from "../constants/zambianCities";

// Leaflet's default marker icons are broken with webpack because the package
// references relative image paths that don't resolve in a CRA build. Point them
// at the CDN copy so pins render correctly out of the box.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Copper price-tag markers — much nicer than the default blue pins for a
// travel marketplace. Each marker shows the nightly price so users can
// price-scan at a glance without clicking.
const priceMarker = (label) =>
  L.divIcon({
    className: "easystay-price-pin",
    html: `<div style="background:#C85A17;color:#fff;padding:5px 10px;border-radius:14px;font-size:12px;font-weight:700;box-shadow:0 2px 8px rgba(200,90,23,.45);white-space:nowrap;border:2px solid #fff">${label}</div>`,
    iconSize: [null, 26],
    iconAnchor: [30, 13],
  });

const cityCoords = Object.fromEntries(
  ZAMBIAN_CITIES.map((c) => [c.name.toLowerCase(), [c.lat, c.lng]])
);

// Resolve a property's coordinates. Uses `latitude`/`longitude` if the doc
// carries them, otherwise falls back to the property's city centroid with a
// small deterministic jitter so co-located pins don't overlap.
function propertyCoords(p) {
  if (typeof p.latitude === "number" && typeof p.longitude === "number") {
    return [p.latitude, p.longitude];
  }
  const base = cityCoords[(p.city || "").toLowerCase()] || [-15.3875, 28.3228];
  const seed = String(p.id || p.title || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const j1 = ((seed % 71) / 71 - 0.5) * 0.04;
  const j2 = ((seed % 113) / 113 - 0.5) * 0.04;
  return [base[0] + j1, base[1] + j2];
}

/** Click-to-drop center handler for draw-radius mode. */
function DrawHandler({ enabled, onCenter }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onCenter([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

/**
 * PropertyMap — inline OpenStreetMap view of a listing set.
 *
 * Props:
 *   properties     — array of Property objects
 *   height         — CSS pixel height (default 500)
 *   userCoords     — optional {lat,lng} to also drop a "you are here" marker
 *   drawMode       — when true, next map click sets the radius filter center
 *   radiusCenter   — [lat, lng] tuple; renders a copper circle at this point
 *   radiusKm       — number of km for the circle radius (default 5)
 *   onCenterChange — callback([lat,lng]) fired when a click lands in drawMode
 */
export default function PropertyMap({
  properties = [],
  height = 500,
  userCoords = null,
  drawMode = false,
  radiusCenter = null,
  radiusKm = 5,
  onCenterChange,
}) {
  const points = useMemo(
    () => properties.map((p) => ({ p, coords: propertyCoords(p) })),
    [properties]
  );

  const center = useMemo(() => {
    if (radiusCenter) return radiusCenter;
    if (userCoords) return [userCoords.lat, userCoords.lng];
    if (points.length === 0) return [-15.3875, 28.3228];
    const lat = points.reduce((s, x) => s + x.coords[0], 0) / points.length;
    const lng = points.reduce((s, x) => s + x.coords[1], 0) / points.length;
    return [lat, lng];
  }, [points, userCoords, radiusCenter]);

  const zoom = radiusCenter ? 10 : userCoords ? 11 : points.length === 1 ? 13 : points.length <= 4 ? 8 : 6;

  return (
    <div
      data-testid="property-map"
      className="overflow-hidden rounded-3xl card-shadow"
      style={{ height, cursor: drawMode ? "crosshair" : "grab" }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <DrawHandler enabled={drawMode} onCenter={onCenterChange || (() => {})} />
        {radiusCenter && (
          <Circle
            center={radiusCenter}
            radius={Math.max(0.5, Number(radiusKm) || 5) * 1000}
            pathOptions={{ color: "#C85A17", weight: 2, fillColor: "#C85A17", fillOpacity: 0.08 }}
          />
        )}
        {userCoords && (
          <Marker
            position={[userCoords.lat, userCoords.lng]}
            icon={L.divIcon({
              className: "easystay-you-are-here",
              html: '<div style="background:#0f3d2e;color:#fff;padding:5px 10px;border-radius:14px;font-size:11px;font-weight:700;box-shadow:0 2px 8px rgba(15,61,46,.5);white-space:nowrap;border:2px solid #fff">You&nbsp;are&nbsp;here</div>',
              iconSize: [null, 26],
              iconAnchor: [50, 13],
            })}
          >
            <Popup>Your current location</Popup>
          </Marker>
        )}
        {points.map(({ p, coords }) => (
          <Marker
            key={p.id}
            position={coords}
            icon={priceMarker(`K${Number(p.price_per_night_zmw || 0).toLocaleString()}`)}
          >
            <Popup>
              <div style={{ minWidth: 200 }} data-testid={`map-popup-${p.id}`}>
                <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 14 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                  {p.city} · {(p.type || "stay").replace("_", " ")}
                </div>
                <div style={{ color: "#C85A17", fontWeight: 700, fontSize: 15 }}>
                  K{Number(p.price_per_night_zmw || 0).toLocaleString()} / night
                </div>
                <Link
                  to={`/property/${p.id}`}
                  style={{ display: "inline-block", marginTop: 8, color: "#0f3d2e", fontSize: 12, fontWeight: 700 }}
                >
                  View details →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

/** Haversine distance (km) between two [lat, lng] tuples — used by Search
 * to filter listings inside a user-drawn radius. Exported for reuse. */
export function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1 + s2));
}

/** Resolve a property to [lat, lng] the same way the map does — exported so
 * Search.jsx can apply the exact same coordinate resolution when filtering. */
export function resolvePropertyCoords(p) {
  return propertyCoords(p);
}
